import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, TrendingUp, Users, Award, Sparkles } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

function Kpi({ icon: Icon, value, label, suffix }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur"
        >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-orange/15 text-brand-orange">
                <Icon className="h-6 w-6" />
            </div>
            <div className="mt-6 font-display text-6xl font-normal tracking-tightest text-white">
                {value}
                {suffix && <span className="text-3xl text-brand-orange">{suffix}</span>}
            </div>
            <div className="mt-2 text-sm text-white/60">{label}</div>
        </motion.div>
    );
}

export default function Indicateurs() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/indicateurs/stats")
            .then((r) => setStats(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-brand-coal text-white">
            <SiteNav variant="dark" />
            <section className="relative overflow-hidden border-b border-white/10 px-6 pt-32 pb-24">
                <div className="absolute inset-0 -z-10 grain opacity-30" />
                <div className="mx-auto max-w-6xl">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-orange">
                        Démarche qualité Qualiopi
                    </div>
                    <h1 className="mt-4 font-display text-6xl font-normal leading-[1.05] tracking-tightest md:text-7xl">
                        Indicateurs de <span className="text-brand-orange">performance</span>.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-white/70">
                        Cursus Physio mesure la qualité de ses formations en continu.
                        Voici les indicateurs publics, mis à jour mensuellement, qui attestent
                        de l'exigence de l'Institut de la Prévention Active.
                    </p>
                </div>
            </section>

            <section className="px-6 py-20" data-testid="indicateurs-kpis">
                <div className="mx-auto max-w-6xl">
                    {loading ? (
                        <div className="text-center text-white/50">Chargement…</div>
                    ) : stats?.ready === false ? (
                        <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center">
                            <Sparkles className="mx-auto h-10 w-10 text-brand-orange" />
                            <h2 className="mt-6 font-display text-3xl">Indicateurs en cours de constitution</h2>
                            <p className="mx-auto mt-4 max-w-xl text-white/60">
                                {stats.next_milestone || "Indicateurs publics disponibles dès la première vague de retours stagiaires."}
                            </p>
                            <div className="mx-auto mt-8 grid max-w-3xl grid-cols-2 gap-4 md:grid-cols-4 text-left">
                                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                    <div className="font-display text-3xl text-brand-orange">{stats.sessions_done ?? 0}</div>
                                    <div className="mt-1 text-xs text-white/50">Sessions réalisées</div>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                    <div className="font-display text-3xl text-brand-orange">{stats.stagiaires_formed ?? 0}</div>
                                    <div className="mt-1 text-xs text-white/50">Stagiaires formés</div>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                    <div className="font-display text-3xl text-brand-orange">{stats.satisfaction_responses ?? 0}</div>
                                    <div className="mt-1 text-xs text-white/50">Retours collectés</div>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                    <div className="font-display text-3xl text-brand-orange">12</div>
                                    <div className="mt-1 text-xs text-white/50">Formateurs experts</div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                            <Kpi
                                icon={Award}
                                value={stats.satisfaction_avg_5 ?? "—"}
                                suffix="/5"
                                label="Satisfaction stagiaire moyenne"
                            />
                            <Kpi
                                icon={TrendingUp}
                                value={stats.nps ?? "—"}
                                label="Net Promoter Score (NPS)"
                            />
                            <Kpi
                                icon={ShieldCheck}
                                value={stats.success_rate_pct ?? "—"}
                                suffix="%"
                                label="Taux de réussite (post-acquis)"
                            />
                            <Kpi
                                icon={Users}
                                value={stats.stagiaires_formed ?? 0}
                                label="Stagiaires formés"
                            />
                        </div>
                    )}

                    <div className="mt-20 grid gap-12 lg:grid-cols-2">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                                Méthodologie
                            </div>
                            <h2 className="mt-3 font-display text-4xl font-normal tracking-tightest">
                                Comment nous calculons ces indicateurs.
                            </h2>
                            <ul className="mt-6 space-y-4 text-white/70">
                                <li className="border-l-2 border-brand-orange/50 pl-5">
                                    <span className="font-semibold text-white">Satisfaction</span> — moyenne des questionnaires Qualiopi de satisfaction à chaud, remplis en fin de session par les stagiaires. Note sur 100 convertie en /5.
                                </li>
                                <li className="border-l-2 border-brand-orange/50 pl-5">
                                    <span className="font-semibold text-white">Net Promoter Score</span> — recodage de la question "Je recommanderais cette formation à un confrère" : promoteurs (4-5), neutres (3), détracteurs (1-2). NPS = % promoteurs − % détracteurs.
                                </li>
                                <li className="border-l-2 border-brand-orange/50 pl-5">
                                    <span className="font-semibold text-white">Taux de réussite</span> — pourcentage des stagiaires ayant validé l'évaluation post-acquis avec un score ≥ 70%.
                                </li>
                                <li className="border-l-2 border-brand-orange/50 pl-5">
                                    <span className="font-semibold text-white">Stagiaires formés</span> — nombre cumulé d'inscriptions ayant abouti à une présence effective et un émargement complet.
                                </li>
                            </ul>
                        </div>
                        <div className="rounded-3xl border border-white/10 bg-white/5 p-10">
                            <ShieldCheck className="h-10 w-10 text-brand-orange" />
                            <h3 className="mt-6 font-display text-3xl">Référencement Qualiopi</h3>
                            <p className="mt-4 text-white/70">
                                Cursus Physio opère ses formations dans le cadre d'une convention de partenariat avec le
                                <strong className="text-white"> Centre Axiome</strong>, organisme de formation certifié Qualiopi.
                                À ce titre, nous appliquons les 7 critères du Référentiel National Qualité, soumis chaque année
                                à audit indépendant.
                            </p>
                            <div className="mt-6 rounded-2xl bg-brand-orange/10 px-5 py-4 text-sm text-white/80">
                                <strong className="text-brand-orange">{stats?.satisfaction_responses ?? 0}</strong> retours stagiaires
                                analysés à ce jour. Méthodologie de calcul détaillée disponible sur demande à
                                <a href="mailto:qualite@cursus-physio.fr" className="ml-1 underline decoration-brand-orange decoration-2 underline-offset-4">
                                    qualite@cursus-physio.fr
                                </a>.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
